'use client';

/**
 * ConsumerAstorProvider
 *
 * Portal-specific Astor integration for the Consumer Portal.
 * Wraps AstorContextProvider with consumer portal tRPC hooks and auth context.
 *
 * Key differences from Business Portal:
 * - AstorOnboardingRail included for guided consumer onboarding (loan application
 *   walkthrough, document submission steps, etc.)
 * - Astor messages focus on loan application journey
 * - Chat flyover triggered from Header sparkle icon (same pattern as business portal)
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { AstorActivityTracker, AstorContextProvider, AstorOnboardingRail } from '@ops-matrix/design-system-ui';
import type { AstorAction, AstorEvent, AstorNudge, OnboardingPlan, PageContextInput } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import env from '../../config/env';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { getAccessToken } from '../../lib/auth/storage';
import { trpc } from '../../lib/trpc/client';

// ============================================================================
// TYPES
// ============================================================================

interface ConsumerAstorProviderProps {
  children: React.ReactNode;
}

/**
 * Map consumer types to PersonaType values used by the Astor onboarding system.
 * ConsumerType values from shared-types don't directly map to PersonaType,
 * so we translate them here.
 */
const CONSUMER_TO_PERSONA: Record<string, string> = {
  LOAN_APPLICANT: 'LOAN_APPLICANT',
  END_CONSUMER: 'LOAN_APPLICANT', // Default consumers to loan applicant persona
  end_consumer: 'LOAN_APPLICANT',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function ConsumerAstorProvider({ children }: ConsumerAstorProviderProps) {
  const { consumer, isAuthenticated } = useConsumerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialPlan, setInitialPlan] = useState<OnboardingPlan | null>(null);

  // ---- tRPC hooks ----

  const { data: planData } = trpc.astor.getOnboardingPlan.useQuery(
    { portal: 'consumer' },
    { enabled: isAuthenticated },
  );

  const generatePlanMutation = trpc.astor.generateOnboardingPlan.useMutation();
  const completeMilestoneMutation = trpc.astor.completeMilestone.useMutation();
  const dismissOnboardingMutation = trpc.astor.dismissOnboarding.useMutation();
  const syncActivityMutation = trpc.astor.syncActivity.useMutation();

  // Pre-fetch the onboarding plan on mount
  useEffect(() => {
    if (planData) {
      setInitialPlan(planData as OnboardingPlan);
    }
  }, [planData]);

  // ---- Build Astor user from consumer auth context ----

  const astorUser = useMemo(() => {
    if (!consumer) return null;
    const consumerType = consumer.consumerType ?? 'END_CONSUMER';
    const persona = CONSUMER_TO_PERSONA[consumerType] ?? 'LOAN_APPLICANT';
    return {
      id: consumer.id,
      role: consumerType,
      businessId: consumer.businessId,
      realmId: consumer.realmId ?? '',
      persona,
      subscriptionTier: 'starter',
    };
  }, [consumer]);

  // ---- Callback handlers wired to tRPC ----

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  const handleCompleteMilestone = useCallback(
    async (milestoneId: string): Promise<void> => {
      await completeMilestoneMutation.mutateAsync({
        milestoneId,
        portal: 'consumer',
      });
    },
    [completeMilestoneMutation],
  );

  const handleDismissOnboarding = useCallback(async (): Promise<void> => {
    await dismissOnboardingMutation.mutateAsync({ portal: 'consumer' });
  }, [dismissOnboardingMutation]);

  const handleGeneratePlan = useCallback(async (): Promise<OnboardingPlan> => {
    await generatePlanMutation.mutateAsync({ portal: 'consumer' });
    // The mutation returns { requested: true }, not the plan itself.
    // Plan will be fetched via the query on next poll/refetch.
    // Return empty plan structure as placeholder.
    const consumerType = consumer?.consumerType ?? 'END_CONSUMER';
    const persona = CONSUMER_TO_PERSONA[consumerType] ?? 'LOAN_APPLICANT';
    const now = new Date().toISOString();
    return {
      id: '',
      userId: consumer?.id ?? '',
      businessId: consumer?.businessId ?? '',
      portal: 'consumer',
      persona: persona as OnboardingPlan['persona'],
      generatedAt: now,
      generatedBy: 'ai' as OnboardingPlan['generatedBy'],
      sections: [],
    };
  }, [generatePlanMutation, consumer?.id, consumer?.businessId, consumer?.consumerType]);

  const handleSyncActivity = useCallback(
    async (events: AstorEvent[]): Promise<void> => {
      // Filter out events with empty route (Zod requires route.length >= 1)
      const validEvents = events
        .map(({ type, timestamp, route, metadata }) => ({
          type,
          timestamp,
          route: route || '/',
          ...(metadata ? { metadata } : {}),
        }))
        .filter((e) => e.type && e.timestamp);
      if (validEvents.length === 0) return;
      await syncActivityMutation.mutateAsync({
        portal: 'consumer',
        events: validEvents,
      });
    },
    [syncActivityMutation],
  );

  const utils = trpc.useUtils();

  const handleGetNudge = useCallback(
    async (context: PageContextInput): Promise<AstorNudge | null> => {
      return utils.astor.getNudgeForContext.fetch(context) as Promise<AstorNudge | null>;
    },
    [utils],
  );

  const handleGetSuggestions = useCallback(
    async (context: PageContextInput): Promise<Array<{ label: string; action: AstorAction }> | null> => {
      const suggestions = await utils.astor.getContextualSuggestions.fetch(context);
      if (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0) return null;
      return suggestions as Array<{ label: string; action: AstorAction }>;
    },
    [utils],
  );

  const chatConfig = useMemo(
    () => ({
      endpoint: `${env.VITE_API_URL}/agents/consumer-assistant/invoke/stream`,
      getAuthToken: () => getAccessToken(),
    }),
    [],
  );

  // ---- Render ----

  // Don't render Astor context until user is authenticated
  if (!isAuthenticated || !astorUser) {
    return <>{children}</>;
  }

  return (
    <AstorContextProvider
      portal="consumer"
      user={astorUser}
      onNavigate={handleNavigate}
      onCompleteMilestone={handleCompleteMilestone}
      onDismissOnboarding={handleDismissOnboarding}
      onGeneratePlan={handleGeneratePlan}
      onSyncActivity={handleSyncActivity}
      onGetNudge={handleGetNudge}
      onGetSuggestions={handleGetSuggestions}
      chatConfig={chatConfig}
      initialPlan={initialPlan}
      maxNudgesPerSession={3}
    >
      {children}

      {/* Activity tracker — invisible, tracks route changes and idle time */}
      <AstorActivityTracker route={location.pathname} />

      {/* Onboarding rail — guided consumer onboarding (loan application walkthrough, etc.) */}
      <AstorOnboardingRail />
    </AstorContextProvider>
  );
}

export default ConsumerAstorProvider;
